import { getIncomers } from '@xyflow/react';
import { getAllImagesFromNodes, getTextFromTextNodes, getPromptFromToolNodes, getPromptFromUploadNodes, getSubjectSystemHintsFromUploadNodes, getUploadNodeImages } from '@/lib/xyflow';
import { buildImageProviderOptionsForRequest } from '@flowmuse/models/image/build-image-provider-options';
import { handleError } from '@/lib/error/handle';
import { imageModels, getEffectiveImageConfig, getEffectiveImagePrecheck } from '@flowmuse/models/image';
import { env } from '@/lib/env';
import { ensureProjectSavedOrShowRefreshModal } from '@/lib/project-save';
import { checkConcurrency } from '@/lib/check-concurrency';
import { isAssetRefSupportedModel } from '@/lib/xyflow';
import { normalizeMentions } from '@/components/nodes/shared/normalize-mentions';

function resolveAssetRefUrls(
    images: { url: string; type: string }[],
    incomers: any[],
    inlineAtts: any[],
): { url: string; type: string }[] {
    return images.map((img) => {
        if (!img.url.startsWith('Asset://')) return img;
        for (const inc of incomers) {
            const d = inc?.data;
            const urls = [d?.generated?.url, d?.generated?.urls?.[0], d?.content?.url].filter(Boolean);
            if (urls.includes(img.url) && d?.assetPreviewUrl) {
                return { ...img, url: d.assetPreviewUrl };
            }
        }
        for (const a of inlineAtts) {
            if (a?.url === img.url && a?.previewUrl) return { ...img, url: a.previewUrl };
        }
        return img;
    });
}

function resolveImageInputFromNodeData(data: any): { url: string; type: string } | undefined {
    if (!data) return undefined;
    const generated = data?.generated;
    const content = data?.content;
    if (data?.viewMode === 'primitive') return content?.url ? content : undefined;

    const generatedUrl = generated?.url || generated?.sizeUrl || generated?.urls?.[0];
    if (data?.viewMode === 'transform' || generatedUrl) {
        return generatedUrl ? { ...generated, url: generatedUrl, type: generated?.type || 'image/png' } : undefined;
    }

    return content?.url ? content : undefined;
}

// ========================== 类型定义 ==========================
// 翻译函数类型
type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;
// 图片生成处理函数入参类型
export type CreateImageHandleGenerateParams = {
    // 项目 ID
    project: { id: string };
    // 节点 ID
    nodeId: string;
    // 模型 ID
    modelId: string;
    // 节点数据
    data: any;
    // 获取所有节点回调
    getNodes: () => any[];
    // 获取所有边回调
    getEdges: () => any[];
    // 更新节点数据回调
    updateNodeData: (id: string, partial: any) => void;
    // 翻译函数
    t: TranslateFn;
};

// 创建图片生成处理函数
export const createImageHandleGenerate = (params: CreateImageHandleGenerateParams) => {
    // 解构入参
    const { project, nodeId, modelId, data, getNodes, getEdges, updateNodeData, t } = params;
    // 工厂函数, 返回 handleGenerate 函数
    const handleGenerate = async () => {
        try {
            // ========================== 生成前强制保存并等待（严格） ==========================
            // 保存失败/超时：不继续生成，并提示用户刷新
            const ok = await ensureProjectSavedOrShowRefreshModal(10000);
            if (!ok) return;

            // ========================== 节点状态兜底 ==========================
            // 如果项目 ID 不存在, 则抛出错误
            if (!project.id) {
                throw new Error(t('Project not found'));
            }
            // 如果节点 ID 不存在, 则抛出错误
            if (!nodeId) {
                throw new Error(t('Node not found'));
            }
            // 如果模型 ID 不存在, 则抛出错误
            if (!modelId) {
                throw new Error(t('Model not found'));
            }
            // 如果节点状态为 processing, 则抛出错误
            if ((data as any)?.status === 'processing') {
                throw new Error(t('Loading'));
            }

            // ========================== 并发检查 ==========================
            if (!await checkConcurrency(project.id)) return false;

            // ========================== 获取模型元数据 ==========================
            const modelMeta = imageModels[modelId];
            if (!modelMeta) {
                throw new Error(t('Model not supported'));
            }
            const imageMode = (data as any)?.imageMode as string | undefined;

            // ========================== 获取节点数据 ==========================
            // 获取所有上游节点, 兜底 []
            const incomers = getIncomers({ id: nodeId }, getNodes(), getEdges());
            // 获取 incomers 中的文本节点, 兜底 []
            const textPrompts = getTextFromTextNodes(incomers);
            // 获取 incomers 中的工具节点提示词（例如 camera tool 自动生成的 prompt）
            const toolPrompts = getPromptFromToolNodes(incomers);

            // ========================== 图片输入配置（配置驱动，与视频节点同一套逻辑） ==========================
            // 根据 imageMode 决定是否允许图片输入（inlineMedia 按模式与顶层深度合并）
            const imCfg = (() => {
                if (imageMode === 't2i') return { allow: false as const };
                const modelImg = getEffectiveImageConfig(modelMeta, imageMode).inlineMedia?.image;
                if (modelImg) return { allow: true as const, maxCount: modelImg.maxCount as number | undefined };
                if (modelMeta.supportsEdit) return { allow: true as const, maxCount: 9 };
                return { allow: true as const };
            })();

            // ========================== 收集所有图片输入 ==========================
            // 获取 incomers 中的图片输入（包含 image 节点与 upload 节点中 fileType=image 的内容）, 兜底 []
            // 使用 getAllImagesFromNodes 确保顺序与序号一致（按连接顺序）
            let images = getAllImagesFromNodes(incomers, getEdges(), nodeId);
            // Tool-driven image input: allow tool nodes to act as an image source (via tool.params.source_image_url),
            // and avoid persisting stale URLs on the image node itself.
            const toolImageNode = (() => {
                try {
                    return (Array.isArray(incomers) ? incomers : []).find((n: any) =>
                        n &&
                        n.type === 'tool' &&
                        typeof (n as any)?.data?.tool?.params?.source_image_url === 'string' &&
                        String((n as any).data.tool.params.source_image_url).trim().length > 0
                    );
                } catch {
                    return undefined;
                }
            })();
            const toolImageUrl = (() => {
                try {
                    const u = (toolImageNode as any)?.data?.tool?.params?.source_image_url;
                    return typeof u === 'string' ? u.trim() : '';
                } catch {
                    return '';
                }
            })();
            if ((!images || images.length === 0) && toolImageUrl) {
                images = [{ url: toolImageUrl, type: 'image/png' }] as any;
            }
            // 收集内联图片素材
            const inlineAtts = Array.isArray((data as any)?.inlineAttachments) ? (data as any).inlineAttachments as any[] : [];
            const inlineImages = inlineAtts
                .filter((a: any) => a?.fileType === 'image' && a?.url)
                .map((a: any) => ({ url: a.url, type: a.type || 'image/png' }));
            if (inlineImages.length > 0) {
                images = [...images, ...inlineImages];
            }
            // 按 imageGridOrder 拖拽排序（用户在 DraggableMediaGrid 中手动排列的顺序）
            // rid 约定：
            //   普通上游：up:${nodeId}
            //   multi 主体上游：up:${nodeId}（首图，向后兼容）+ up:${nodeId}:1, :2, ... （其余原图）
            //   inline：in:${attId}
            const imageGridOrder = (data as any)?.imageGridOrder as string[] | undefined;
            if (Array.isArray(imageGridOrder) && imageGridOrder.length > 0) {
                const upstreamImageByRid = new Map<string, { url: string; type: string }>();
                for (const inc of incomers as any[]) {
                    const nt = String(inc.type || '');
                    const d = inc.data as any;
                    const ridBase = `up:${String(inc.id)}`;
                    if (nt === 'image') {
                        const img = resolveImageInputFromNodeData(d);
                        if (img?.url) upstreamImageByRid.set(ridBase, { url: img.url, type: img.type || 'image/png' });
                    } else if (nt === 'upload' && d?.fileType === 'image') {
                        const expanded = getUploadNodeImages(d);
                        expanded.forEach((img, i) => {
                            const rid = i === 0 ? ridBase : `${ridBase}:${i}`;
                            upstreamImageByRid.set(rid, { url: img.url, type: img.type || 'image/png' });
                        });
                    }
                }
                const inlineImageById = new Map<string, { url: string; type: string }>();
                for (const a of inlineAtts) {
                    if (a?.fileType === 'image' && a?.url) inlineImageById.set(a.id, { url: a.url, type: a.type || 'image/png' });
                }
                const ordered: { url: string; type: string }[] = [];
                const usedUrls = new Set<string>();
                for (const rid of imageGridOrder) {
                    let entry: { url: string; type: string } | undefined;
                    if (rid.startsWith('up:')) entry = upstreamImageByRid.get(rid);
                    else if (rid.startsWith('in:')) entry = inlineImageById.get(rid.slice(3));
                    if (entry && !usedUrls.has(entry.url)) { ordered.push(entry); usedUrls.add(entry.url); }
                }
                for (const img of images) {
                    if (!usedUrls.has(img.url)) ordered.push(img);
                }
                images = ordered;
            }

            // ========================== 非 SD 系列模型：将 Asset:// 解析为预览 URL ==========================
            if (!isAssetRefSupportedModel(modelId)) {
                images = resolveAssetRefUrls(images, incomers, inlineAtts);
            }

            // ========================== 根据 imCfg 过滤图片（与视频节点一致） ==========================
            if (!imCfg.allow) {
                images = [];
            } else if (imCfg.maxCount != null && Number.isFinite(imCfg.maxCount)) {
                images = images.slice(0, imCfg.maxCount);
            }

            // 如果节点包含局部重绘遮罩，将遮罩 base url 作为第二个元素传入（仅 i2i 模式有效）
            const maskBaseUrl = imCfg.allow ? ((data as any)?.inpaintMask as string | undefined) : undefined;
            const imagesForPayload = (() => {
                if (!maskBaseUrl || typeof maskBaseUrl !== 'string') return images;
                if (images.length >= 1) {
                    // 按约定：原图作为第一个，遮罩作为第二个
                    return [images[0], { url: maskBaseUrl }, ...images.slice(1)];
                }
                // 没有上游图像时，也传入遮罩（后端将按支持情况处理）
                return [{ url: maskBaseUrl }];
            })();
            // 收集来自上传节点/上游素材的提示词（例如主体库写入的 prompt），兜底 []
            const subjectPrompts = getPromptFromUploadNodes(incomers);

            // 获取 image node 中的 instructions, 兜底 ''
            const instructions = (data as any)?.instructions ?? '';
            // 本节点额外 prompt（非输入框编辑；插件/系统写入）
            const nodePrompt = (data as any)?.nodePrompt ?? '';
            // precheck 按当前模式取（precheckByMode[imageMode] 优先，回退到顶层 precheck）。
            // 旧节点可能没有 imageMode：按实际是否有图片输入推断，保持与旧行为一致。
            const effectiveMode = imageMode ?? (images.length > 0 ? 'i2i' : 't2i');
            const precheck = getEffectiveImagePrecheck(modelMeta, effectiveMode);
            // 判断是否满足 precheck 条件
            for (const p of precheck) {
                // Prompt can come from:
                // - node.instructions
                // - node.nodePrompt (plugin/system)
                // - upstream text nodes
                // - upstream tool nodes (e.g. camera tool)
                // - upstream upload prompts (subject library, etc.)
                if (p === 'prompt' && (
                    instructions.length === 0 &&
                    nodePrompt.length === 0 &&
                    textPrompts.length === 0 &&
                    toolPrompts.length === 0 &&
                    subjectPrompts.length === 0
                )) {
                    throw new Error('Please input prompt');
                }
                if (p === 'image' && images.length === 0) {
                    throw new Error('Please input image');
                }
            }
            // 获取 imagesCount, 兜底 1
            const imagesCount = Math.max(1, Number((data as any)?.imagesCount || 1));
            // 获取 size, 如果未设置或未传图但 size 为 'match' 时, 兜底 '1024x1024'
            const rawSize = (data as any)?.size;
            const selectedSize = (!rawSize || (images.length === 0 && rawSize === 'match')) ? '1024x1024' : rawSize;
            // 构建 providerOptions, 兜底 {}
            const modelUiRaw = (data as any)?.modelUi ?? {};
            // If this image node is driven by a connected tool image input, strip any persisted source_image_url
            // to prevent "stale" inputs after users disconnect edges later.
            const modelUi = (() => {
                try {
                    if (!toolImageNode) return modelUiRaw;
                    const next: any = { ...(modelUiRaw as any) };
                    try { delete next.source_image_url; } catch { next.source_image_url = undefined; }
                    return next;
                } catch {
                    return modelUiRaw;
                }
            })();
            const providerOptions = buildImageProviderOptionsForRequest({
                modelId,
                modelUi,
                image_resolution: (data as any)?.image_resolution,
            });

            // ========================== 组装 payload ==========================
            // 文生图 or 图生图
            const isEdit = images.length > 0;
            const endpoint = isEdit ? '/api/image/edit' : '/api/image/create';
            const subjectSystemHints = getSubjectSystemHintsFromUploadNodes(incomers);
            const finalSubjectPrompts = subjectSystemHints.length > 0 ? [] : subjectPrompts;
            const buildPrompt = (...segments: string[]) =>
                segments
                    .map((s) => (typeof s === 'string' ? s.trim() : ''))
                    .filter((s) => s.length > 0)
                    .join('\n');
            const prompt = normalizeMentions(buildPrompt(instructions, nodePrompt, ...toolPrompts, ...textPrompts, ...finalSubjectPrompts, ...subjectSystemHints));
            const payload = {
                projectId: project.id,
                nodeId,
                modelId,
                prompt,
                size: selectedSize,
                providerOptions,
                imagesCount,
                ...(isEdit ? { images: imagesForPayload } : {}),
            };

            // ========================== 本地更新 node data 为 processing 状态 ==========================
            // 仅设置需要变更的字段，updateNodeData 会浅合并，避免展开陈旧闭包数据
            updateNodeData(nodeId, {
                modelUi,
                status: 'processing',
                updatedAt: new Date().toISOString(),
                error: undefined,
                runtimeEstimatedTime: undefined,
                // 清除上一次生成残留的追踪 id：否则轮询会立刻用「旧的、已完成的」generationId
                // 查到 completed + 旧结果，瞬间把节点标记完成并显示旧图，而真正新发起的生成仍在后台运行。
                // 清空后本次生成先进入 nodeId 兜底模式（带 30s 门槛），待 202 写入新 generationId 后再正常轮询。
                processingGenerationId: undefined,
                processingTaskId: undefined,
            });

            // ========================== 请求生成 ==========================
            const rawResponse = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-internal-secret': env.NEXT_PUBLIC_INTERNAL_API_SECRET },
                body: JSON.stringify(payload),
                signal: AbortSignal.timeout(50_000),
            });

            // ========================== 解析响应体 ==========================
            const response = (await rawResponse.json()) as { nodeData?: any; error?: string; queued?: boolean; generationId?: string; estimatedTime?: number };
            // rawResponse 失败, rawResponse json 解析失败, response.error 不为空, 则抛出错误 (优先展示 API 返回的 error)
            if (!rawResponse.ok || !response || response.error) {
                const message = response.error || 'Server Request failed';
                throw new Error(message);
            }

            // ========================== 非队列模式 ==========================
            // 如果 response.nodeData 存在, 则本地更新 node data
			if ((response as any).nodeData) {
				updateNodeData(nodeId, (response as any).nodeData);
			}

            // ========================== 队列模式 ==========================
			// 写入 generationId, 将逻辑交给 Realtime 或 transform
			if (rawResponse.status === 202) {
                // 获取 generationId
				const generationId = (response as any).generationId;
                // 如果 generationId 存在, 则写入 node data
				if (generationId) {
					updateNodeData(nodeId, { 
                        processingGenerationId: generationId,
                        runtimeEstimatedTime: (response as any).estimatedTime,
                    });
				} else {
                    // 如果 generationId 不存在, 则抛出错误
					throw new Error('Generation ID not found');
				}
				return true;
			}
            return true;

        // ========================== 异常处理 ==========================
        } catch (error) {
            // 获取错误消息
            const errorMessage = (error instanceof Error ? error.message || 'Error generating image' :  'Error generating image');
            // 本地更新 node data 为 failed 状态（只设置必要字段，避免用闭包旧 data 覆盖当前节点状态）
            try {
                updateNodeData(nodeId, {
                    status: 'failed',
                    updatedAt: new Date().toISOString(),
                    error: t(errorMessage),
                    processingGenerationId: undefined,
                    runtimeEstimatedTime: undefined,
                });
            } catch {}
            // 余额不足则打开购买积分弹窗
            if (/(insufficient)/i.test(errorMessage)) {
                try { window.dispatchEvent(new Event('open_buy_credits')); } catch { }
            }
            // 模型权限不足则打开订阅界面
            if (/(not available for your current plan)/i.test(errorMessage)) {
                try { window.dispatchEvent(new Event('open_plans_pricing')); } catch { }
            }
            // 弹出错误提示
            handleError(t('Error generating image'), t(errorMessage));
        }
        return false;
    };
    
    // ========================== 返回 handleGenerate ==========================
    return handleGenerate;
};